import { Spinner } from '@heroui/react';

/**
 * The one loading state a whole page shows.
 *
 * ⚠️ IT DELIBERATELY DOES NOT APPEAR FOR A FAST LOAD. `loader-delayed` holds it
 * at zero opacity for 260ms, so a navigation that resolves quickly — which is
 * most of them, the API is on the same origin and the chunks are cached — shows
 * the reader nothing at all rather than a spinner that flashes and is replaced.
 * The complaint that produced this component was not "loading takes too long",
 * it was the swap being visible.
 *
 * ⚠️ AND IT IS CENTRED IN THE CONTENT AREA, not parked in the corner. A page's
 * loading state standing where its content will stand is what stops the layout
 * jumping when the content arrives; `min-h-[60vh]` is enough to hold the space
 * without pushing a phone screen taller than the viewport.
 *
 * `role="status"` + `aria-live="polite"` so a screen reader is told the page is
 * working, once, without interrupting.
 *
 * ⚠️ THE SPINNER ITSELF IS HIDDEN FROM THE ACCESSIBILITY TREE. HeroUI's
 * `Spinner` root is a `role="status"` with `aria-label="Loading"` of its own, so
 * left bare this element announces twice — once as this component's Russian
 * label and once as the library's English one, from a status region nested
 * inside another status region.
 */
export function PageLoader({ label = 'Загрузка…' }: { label?: string }) {
  return (
    <div
      className="loader-delayed grid min-h-[60vh] place-items-center"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3 text-muted">
        <span aria-hidden="true" className="flex">
          <Spinner />
        </span>
        <p className="text-sm">{label}</p>
      </div>
    </div>
  );
}
